import { useEffect, useRef, useState } from 'react';
import { useApp } from '../store';
import { useDismiss, useFocusTrap } from '../motion';
import Icon from './Icon';
import NewProjectWizard from './NewProjectWizard';
import {
  DELIVERY_MODE_LABEL,
  DELIVERY_MODE_BLURB,
  keyFromName,
  tidyKey,
  projectProblem,
} from '../projects';

/**
 * STEP 0 — WHAT KIND OF BOARD IS THIS, CHOSEN BEFORE THE WIZARD.
 *
 * Until now a new board silently booted as a full project — Plan, Finances,
 * Decisions and Lessons all showing — and the project-vs-continuous choice lived
 * only in Settings, so a continuous/BAU board had to be corrected after the fact.
 * This adds one calm first step: a focused "What kind of board is this?" that
 * reuses the approved Settings mode-cards and their exact wording.
 *
 * A CONTINUOUS SERVICE IS PRE-SELECTED (Vinnie, 30 Oct — most boards are ongoing
 * work, not bounded projects, so one click → Continue lands a flow board with no
 * plan scaffolding to shed; a project is one click away). The whole thing is
 * escapable — Escape, Cancel, or a click on the backdrop. The choice then ROUTES
 * the creation flow:
 *
 *   · A project → the existing NewProjectWizard, unchanged (Name it → Phases →
 *     Charter, Charter optional), landing on the Plan.
 *   · A continuous service → a single "Name it" step that sets deliveryMode =
 *     'continuous' and lands on the Board, with no plan/budget/sign-off to set up
 *     — so a BAU board is never born carrying project scaffolding it has to shed.
 *
 * The chooser owns which route is showing; each route owns its own creation.
 */

type Mode = 'project' | 'continuous';

export default function NewBoardFlow({ close }: { close: () => void }) {
  // null = still on Step 0 (the chooser); otherwise the route the choice took.
  const [route, setRoute] = useState<Mode | null>(null);

  if (route === 'project') return <NewProjectWizard close={close} />;
  if (route === 'continuous') return <ContinuousBoard close={close} onBack={() => setRoute(null)} />;
  return <BoardKindChoice onPick={setRoute} close={close} />;
}

// ── Step 0: the choice ───────────────────────────────────────────────────────
function BoardKindChoice({ onPick, close }: { onPick: (m: Mode) => void; close: () => void }) {
  const { leaving, dismiss } = useDismiss(close);
  // A continuous service is the pre-selected default (Vinnie, 30 Oct) — the common
  // case is one click to Continue — but either can be chosen before committing.
  const [choice, setChoice] = useState<Mode>('continuous');
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);

  // Escape closes the dialog from anywhere in it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  return (
    <div
      className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="New board"
      // Click-away on the backdrop closes it, like the other dialogs here.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div ref={panelRef} tabIndex={-1} className={`setup board-kind ${leaving ? 'is-leaving' : ''}`}>
        <div className="setup-top">
          <span className="setup-count">New board</span>
          <button className="board-kind-esc" onClick={dismiss} aria-label="Close">
            ESC
          </button>
        </div>

        <h2>What kind of board is this?</h2>
        <p className="wiz-lead">
          You can change this later in Settings. It decides whether the board carries a plan, budget
          and sign-offs &mdash; or just follows the flow of work.
        </p>

        <div className="mode-cards">
          {(['project', 'continuous'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`mode-card ${choice === m ? 'is-on' : ''}`}
              aria-pressed={choice === m}
              onClick={() => setChoice(m)}
            >
              <span className="mode-card-title">
                {DELIVERY_MODE_LABEL[m]}
                {m === 'continuous' && <span className="person-you">default</span>}
              </span>
              <span className="mode-card-blurb">{DELIVERY_MODE_BLURB[m]}</span>
            </button>
          ))}
        </div>

        <div className="setup-foot">
          <button className="btn" onClick={dismiss}>
            Cancel
          </button>
          <span className="setup-spacer" />
          <button className="btn btn-primary" onClick={() => onPick(choice)}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The continuous route: a single "Name it" step ────────────────────────────
function ContinuousBoard({ close, onBack }: { close: () => void; onBack: () => void }) {
  const { leaving, dismiss } = useDismiss(close);
  const createProject = useApp((s) => s.createProject);
  const setDeliveryMode = useApp((s) => s.setDeliveryMode);
  const setView = useApp((s) => s.setView);
  const projects = useApp((s) => s.projects);

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  // Once the key is theirs, the suggestion stops following the name.
  const [keyIsTheirs, setKeyIsTheirs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef);

  const suggestedKey = keyIsTheirs ? key : keyFromName(name);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dismiss]);

  const create = async () => {
    const wrong = projectProblem({ name, key: suggestedKey }, projects);
    if (wrong) {
      setProblem(wrong);
      return;
    }
    setBusy(true);
    setProblem('');
    try {
      // Born with no project scaffolding: an empty phase list (so none are
      // seeded), and deliveryMode set to continuous right after — createProject
      // has already switched the board underneath, so setDeliveryMode targets the
      // new one. The gating (isContinuous) then keeps Plan, Finances, Decisions
      // and Lessons hidden until it is switched to a project.
      await createProject(name, suggestedKey, { phases: [] });
      await setDeliveryMode('continuous');
      // Land on the Board, not the Plan — a continuous board has no Plan.
      setView('board');
      dismiss();
    } catch (e) {
      const why = e instanceof Error ? e.message : String(e);
      setProblem(
        /permission/i.test(why)
          ? 'Your account is not allowed to start a board here. Ask an administrator.'
          : `It could not be created — ${why}`
      );
      setBusy(false);
    }
  };

  return (
    <div
      className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="New continuous service board"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div ref={panelRef} tabIndex={-1} className={`setup wizard ${leaving ? 'is-leaving' : ''}`}>
        <div className="setup-top">
          <span className="setup-count">A continuous service &middot; one step</span>
          <button className="board-kind-esc" onClick={dismiss} aria-label="Close">
            ESC
          </button>
        </div>

        {/* Phases and Charter are struck through, so it is clear a continuous
            board skips them rather than having lost them. */}
        <ol className="wiz-steps" aria-hidden="true">
          <li className="wiz-step current">
            <span className="wiz-num">1</span>
            <span className="wiz-lab">Name it</span>
          </li>
          <li className="wiz-step skipped">
            <span className="wiz-num">&ndash;</span>
            <span className="wiz-lab">No phases</span>
          </li>
          <li className="wiz-step skipped">
            <span className="wiz-num">&ndash;</span>
            <span className="wiz-lab">No charter</span>
          </li>
        </ol>

        <div className="setup-body">
          <div className="wiz-row2">
            <label className="stack">
              <span>
                Board name <span className="req" aria-hidden="true">*</span>
              </span>
              <input
                className="input"
                autoFocus
                aria-required="true"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="IT Service Desk"
              />
            </label>
            <label className="stack">
              <span>
                Code <span className="req" aria-hidden="true">*</span>
              </span>
              <input
                className="input key-input"
                aria-required="true"
                value={suggestedKey}
                onChange={(e) => {
                  setKeyIsTheirs(true);
                  setKey(tidyKey(e.target.value));
                }}
                placeholder="ISD"
              />
            </label>
          </div>
          <span className="hint wiz-keyhint">
            {suggestedKey
              ? `Issues will be numbered ${suggestedKey}#1, ${suggestedKey}#2, and so on.`
              : 'A short key goes in front of every issue number.'}
          </span>

          <div className="reuse-hook">
            <span className="reuse-hook-ico">
              <Icon name="check" size={16} />
            </span>
            <div>
              <p className="reuse-hook-h">No plan, budget or sign-off to set up</p>
              <p className="reuse-hook-b">
                A continuous service skips Phases and Charter and opens straight on the Board &mdash;
                Plan, Finances, Decisions and Lessons stay hidden until you switch it to a project.
              </p>
            </div>
          </div>
        </div>

        {problem && (
          <p className="feedback-problem" role="alert">
            {problem}
          </p>
        )}

        <div className="setup-foot">
          <button className="btn" onClick={onBack} disabled={busy}>
            Back
          </button>
          <span className="setup-spacer" />
          <button className="btn btn-primary" onClick={() => void create()} disabled={busy}>
            {busy ? 'Creating…' : 'Create & open board'}
          </button>
        </div>
      </div>
    </div>
  );
}
